/* eslint-disable no-nested-ternary */
/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function, max-statements */
import useInferLabelCreditByUrl from "hooks/useInferLabelCreditByUrl";
import useNewsCritics from "hooks/useNewsCritics";
import usePreviews from "hooks/usePreviews";
import usePreviewsNewsCritics from "hooks/usePreviewsNewsCritics";
import useRegenerateScreenshotMetadataField from "hooks/useRegenerateScreenshotMetadataField";
import useReviews from "hooks/useReviews";
import { useCallback, useEffect, useMemo, useState } from "react";
import regenerateScreenshotMetadataField from "./regenerateScreenshotMetadataField";
const DEFAULT_IS_MODAL_OPEN = false;
/**
 * getImageSelectorItemKey
 *
 * @description Builds a stable comparison key for image/screenshot selector items.
 * @param {ImageSelectorItemForKey} item - Selector item used to build the key.
 * @returns {string} - Stable key composed from url/contentId/role.
 *
 */
const getImageSelectorItemKey = (item) => `${item.originalUrl ?? ""}-${item.contentId ?? ""}-${item.role ?? ""}`;
/**
 * useImageSelector
 *
 * @description Builds the ImageSelector props for either news or reviews flows,
 * including screenshot-specific credit inference and modal state handling.
 * @param {Props} props - Hook input options.
 * @param {"news" | "reviews"} props.serviceType - Owning screen service type.
 * @param {"images" | "screenshots"} [props.mediaType] - Selector media mode.
 * @returns {ImageSelectorProps} - Props consumed by the UI ImageSelector component.
 *
 */
const useImageSelector = ({ serviceType, mediaType = "images" }) => {
    const onInferImageCredit = useInferLabelCreditByUrl();
    const { onRegenerateScreenshotMetadataField: onRegenerateMetadataField } = useRegenerateScreenshotMetadataField();
    const { images: newsCriticsImages } = usePreviewsNewsCritics();
    const { images: reviewsImages } = usePreviews();
    const { setImages: newsCriticsSetImages, setScreenshots: newsCriticsSetScreenshots, state: newsCriticsState } = useNewsCritics();
    const { setImages: reviewsSetImages, setScreenshots: reviewsSetScreenshots, state } = useReviews();
    const [isModalOpen, setIsModalOpen] = useState(DEFAULT_IS_MODAL_OPEN);
    /**
     * onModalClose
     *
     * @description Closes the selector modal.
     * @returns {void} - No return value.
     *
     */
    const onModalClose = useCallback(() => {
        setIsModalOpen(false);
    }, []);
    /**
     * onSelectClick
     *
     * @description Opens the selector modal.
     * @returns {void} - No return value.
     *
     */
    const onSelectClick = useCallback(() => {
        setIsModalOpen(true);
    }, []);
    /**
     * mergePreviewAndSelectedImages
     *
     * @description Merges preview items with currently selected items and preserves selection state.
     * @param {ImageSelectorProps["images"]} previewImages - Images loaded from previews.
     * @param {ImageAddInput[]} selectedImages - Images already selected in form state.
     * @returns {ImageSelectorProps["images"]} - Combined list with selected flags.
     *
     */
    const mergePreviewAndSelectedImages = useCallback((previewImages, selectedImages) => {
        const selectedSet = new Set(selectedImages.map(getImageSelectorItemKey));
        const mappedPreviewImages = previewImages.map((image) => ({
            ...image,
            isSelected: selectedSet.has(getImageSelectorItemKey(image))
        }));
        const selectedImagesMissingFromPreview = selectedImages
            .filter((selectedImage) => !mappedPreviewImages.some((previewImage) => getImageSelectorItemKey(previewImage) ===
            getImageSelectorItemKey(selectedImage)))
            .map((selectedImage) => ({ ...selectedImage, isSelected: true }));
        return [...mappedPreviewImages, ...selectedImagesMissingFromPreview];
    }, []);
    const defaultImages = useMemo(() => {
        if (serviceType === "news") {
            if (mediaType === "screenshots") {
                return (newsCriticsState.news.screenshots?.map((screenshot) => ({
                    ...screenshot,
                    isSelected: true
                })) ?? []);
            }
            return mergePreviewAndSelectedImages(newsCriticsImages, newsCriticsState.news.images ?? []);
        }
        if (mediaType === "screenshots") {
            return (state.homeVideoReview.screenshots?.map((screenshot) => ({
                ...screenshot,
                isSelected: true
            })) ?? []);
        }
        return mergePreviewAndSelectedImages(reviewsImages, state.homeVideoReview.images ?? []);
    }, [
        mergePreviewAndSelectedImages,
        newsCriticsState.news.images,
        newsCriticsState.news.screenshots,
        state.homeVideoReview.images,
        state.homeVideoReview.screenshots,
        mediaType,
        newsCriticsImages,
        reviewsImages,
        serviceType
    ]);
    const [images, setImages] = useState(() => defaultImages);
    useEffect(() => {
        let cancelled = false;
        queueMicrotask(() => {
            if (!cancelled) {
                setImages(defaultImages);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [defaultImages]);
    /**
     * onConfirm
     *
     * @description Persists currently selected items back to news/reviews state and closes the modal.
     * @param {ImageSelectorProps["images"]} [selectedImages] - Selected images from selector UI.
     * @returns {void} - No return value.
     *
     */
    const onConfirm = useCallback((selectedImages) => {
        const normalized = selectedImages
            ?.map(({ isSelected, ...item }) => item)
            .filter((item) => Boolean(item)) ?? [];
        const imagesToConfirm = normalized.map((item) => ({
            contentId: item.contentId,
            originalUrl: item.originalUrl,
            role: item.role
        }));
        if (serviceType === "news") {
            if (mediaType === "screenshots") {
                const screenshots = normalized
                    .filter((item) => Boolean(item.originalUrl))
                    .map((item) => ({
                    caption: item.caption?.trim() || undefined,
                    credit: item.credit?.trim() || undefined,
                    contentId: item.contentId,
                    originalUrl: item.originalUrl
                }));
                newsCriticsSetScreenshots(screenshots);
            }
            else {
                newsCriticsSetImages(imagesToConfirm);
            }
        }
        else if (mediaType === "screenshots") {
            const screenshots = normalized
                .filter((item) => Boolean(item.originalUrl))
                .map((item) => ({
                caption: item.caption?.trim() || undefined,
                credit: item.credit?.trim() || undefined,
                contentId: item.contentId,
                originalUrl: item.originalUrl
            }));
            reviewsSetScreenshots(screenshots);
        }
        else {
            reviewsSetImages(imagesToConfirm);
        }
        setImages((prev) => prev.map((image) => ({
            ...image,
            isSelected: selectedImages?.some((selected) => selected.isSelected &&
                selected.originalUrl === image.originalUrl &&
                selected.contentId === image.contentId) ?? false
        })));
        setIsModalOpen(false);
    }, [
        mediaType,
        newsCriticsSetImages,
        newsCriticsSetScreenshots,
        reviewsSetImages,
        reviewsSetScreenshots,
        serviceType
    ]);
    const modal = useMemo(() => ({
        isOpen: isModalOpen,
        name: mediaType === "screenshots" ? "Screenshot Selector" : "Image Selector",
        onClose: onModalClose
    }), [isModalOpen, mediaType, onModalClose]);
    /**
     * onRegenerateScreenshotMetadataField
     *
     * @description Regenerates one screenshot metadata field and updates the matching selected image state.
     * @param {Pick<RegenerateScreenshotMetadataFieldInput, "contentId" | "field" | "id" | "originalUrl">} input - Metadata regeneration input for one screenshot.
     * @returns {Promise<string | undefined>} - Regenerated value when available.
     *
     */
    const onRegenerateScreenshotMetadataField = useCallback(async ({ contentId, field, id, originalUrl }) => {
        const value = await regenerateScreenshotMetadataField({
            contentId,
            field,
            id,
            mediaType,
            newsName: newsCriticsState.news.name ?? undefined,
            newsIMDbId: newsCriticsState.news.IMDbId ?? undefined,
            newsReleaseDate: newsCriticsState.news.releaseDate ?? undefined,
            onRegenerateMetadataField,
            originalUrl,
            reviewName: state.homeVideoReview.name ?? undefined,
            reviewIMDbId: state.homeVideoReview.IMDbId ?? undefined,
            serviceType
        });
        if (!value?.trim()) {
            return undefined;
        }
        const normalizedValue = value.trim();
        setImages((prevImages) => {
            const hasIdMatch = Boolean(id && prevImages.some((image) => image.id === id && image.isSelected));
            const hasContentIdMatch = Boolean(contentId &&
                prevImages.some((image) => image.contentId?.toString() === contentId.toString() &&
                    image.isSelected));
            const hasSelectedMatch = prevImages.some((image) => image.originalUrl === originalUrl && image.isSelected);
            return prevImages.map((image) => {
                const isIdMatch = id ? image.id === id : false;
                const isContentIdMatch = contentId
                    ? image.contentId?.toString() === contentId.toString()
                    : false;
                const isUrlMatch = hasSelectedMatch
                    ? image.originalUrl === originalUrl && image.isSelected
                    : image.originalUrl === originalUrl;
                const isMatch = hasIdMatch
                    ? hasSelectedMatch
                        ? isIdMatch && image.isSelected
                        : isIdMatch
                    : hasContentIdMatch
                        ? hasSelectedMatch
                            ? isContentIdMatch && image.isSelected
                            : isContentIdMatch
                        : isUrlMatch;
                return isMatch
                    ? {
                        ...image,
                        [field]: normalizedValue
                    }
                    : image;
            });
        });
        return normalizedValue;
    }, [
        mediaType,
        newsCriticsState.news.IMDbId,
        newsCriticsState.news.name,
        newsCriticsState.news.releaseDate,
        onRegenerateMetadataField,
        serviceType,
        state.homeVideoReview.IMDbId,
        state.homeVideoReview.name
    ]);
    return useMemo(() => ({
        images,
        modal,
        onConfirm,
        onInferImageCredit: mediaType === "screenshots" ? onInferImageCredit : undefined,
        onRegenerateScreenshotMetadataField: mediaType === "screenshots"
            ? onRegenerateScreenshotMetadataField
            : undefined,
        onSelectClick,
        showCreditInput: mediaType === "screenshots",
        showScreenshotMetadataInputs: false
    }), [
        images,
        mediaType,
        modal,
        onConfirm,
        onInferImageCredit,
        onRegenerateScreenshotMetadataField,
        onSelectClick
    ]);
};
export default useImageSelector;
